import { Button, Card, CardContent, Divider, Typography } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import useAuth from '../../../hooks/useAuth'
import CustomerDialog from './CustomerDialog'
import CustomersGrid from './CustomersGrid'
import CustomerApi from '../../../api/CustomerApi'

function AdminCustomersView() {
    const { isAuthenticated } = useAuth()
    const [fetchedCustomers, setFetchedCustomers] = React.useState(null)
    const [customers, setCustomers] = React.useState(null)
    const [open, setOpen] = React.useState(false)
    const triggerButtonRef = useRef(null) // Ref for the trigger button

    const customerApi = new CustomerApi()

    async function fetchData() {
        if (!isAuthenticated) return
        let customers = await customerApi.adminAllCustomers()
        setFetchedCustomers(customers)
        if (open) {
            handleCloseDialog()
        }
    }

    useEffect(() => {
        fetchData()
    }, [isAuthenticated])

    useEffect(() => {
        if (!fetchedCustomers) return
        let sortableCustomers = [...fetchedCustomers]
        const sortedCustomers = sortableCustomers.sort((a, b) => a.name.localeCompare(b.name))
        setCustomers(sortedCustomers)
    }, [fetchedCustomers])

    // Effect to update the page title with the number of customers
    useEffect(() => {
        if (customers) {
            const numberOfCustomers = customers.length
            document.title = `Kunder (${numberOfCustomers})`
        }
    }, [customers])

    const handleOpenDialog = () => {
        setOpen(true)
    }

    const handleCloseDialog = () => {
        setOpen(false)
        if (triggerButtonRef.current) {
            triggerButtonRef.current.focus() // Return focus to the button that opened the dialog
        }
    }

    if (!customers) return null

    const handleUpdate = async (newCustomerList) => {
        // examine customers and newCustomerList to determine which customers have been updated
        // for now, the only attribute that can be updated is the name
        const updatedCustomers = newCustomerList.filter((newCustomer) => {
            const oldCustomer = customers.find((customer) => customer.id === newCustomer?.id)
            return oldCustomer?.name !== newCustomer?.name
        })
        // if there are updated customers, use the API to update them
        for (const updatedCustomer of updatedCustomers) {
            if (!updatedCustomer?.id) continue
            await customerApi.updateCustomer(updatedCustomer.id, updatedCustomer)
        }

        // if there are new customers, with no id, use the API to create them
        const newCustomers = newCustomerList.filter((newCustomer) => !newCustomer?.id)
        for (const newCustomer of newCustomers) {
            await customerApi.createCustomer(newCustomer.name)
            console.log('Created new customer:', newCustomer)
        }

        // get the updated list of customers
        await fetchData()
    }

    const handleCreateCustomer = async (newCustomer) => {
        await handleUpdate([...customers, newCustomer])
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>Kunder</title>
            </Helmet>
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                Kunder {customers ? `(${customers.length})` : ''}
            </Typography>
            <Button
                variant='contained'
                color='primary'
                onClick={handleOpenDialog}
                ref={triggerButtonRef} // Assign ref to the trigger button
            >
                Skapa ny kund
            </Button>
            <Divider my={6} />
            <Card mb={6}>
                <CardContent>
                    <CustomersGrid customers={customers} onUpdate={handleUpdate} />
                </CardContent>
            </Card>
            <CustomerDialog open={open} onClose={handleCloseDialog} onSubmit={handleCreateCustomer} />
        </React.Fragment>
    )
}

export default AdminCustomersView
